import React, { useCallback, useEffect, useMemo, useState } from "react";
import { SCORING_BASE_ROOT_URL } from "src/config/constants";
import { COLOR_ARRAY } from "src/config/styleConfig";
import useAuthorizationClient from "src/hooks/useAuthorizationClient";
import { useWeb3Connect } from "src/wallet-connection/context/we3-connect-context";

const UserMonitoringContext = React.createContext({});

const UserMonitoringProvider = ({ children }) => {
  const { address } = useWeb3Connect();
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [liquidateUserData, setLiquidateUserData] = useState({});

  const [assetOverview, setAssetOverview] = useState({});
  const [liquidationHistory, setLiquidationHistory] = useState({});

  const [totalValueLocked, setTotalValueLocked] = useState({});
  const [totalValueLockedLoading, setTotalValueLockedLoading] = useState(false);

  const [transactionHistory, setTransactionHistory] = useState({});

  const client = useAuthorizationClient(`${SCORING_BASE_ROOT_URL}/pool-analysis`);

  const fetchLiquidateUserData = async (pool, address) => {
    try {
      const data = await client.get(`/user/value-user?address=${address}&lending=${pool}`);
      let _data = liquidateUserData;
      _data[pool] = {
        borrow: data.borrow,
        deposit: data.deposit,
        healthFactor: data.health_factor,
        liquidationT: data.liquidationT,
        ltv: data.ltv,
        apy: data.apy * 100,
      };
      setLiquidateUserData({ ..._data });
    } catch (error) {
      console.error(error);
    }
  };

  const _fetchAssetOverview = async (pool, params, active) => {
    try {
      const _params = params;
      _params["lending"] = pool;
      _params["address"] = selectedAddress;
      const data = await client.get("/user/value-volatility-wallet", { params: _params });
      const deposit = [];
      const borrow = [];
      const _data = Object.entries(data);
      for (var item of _data) {
        const timestamp = item[0] * 1000;
        const _deposit = item[1]["deposit"];
        const _borrow = item[1]["borrow"];
        deposit.push([timestamp, _deposit]);
        borrow.push([timestamp, _borrow]);
      }
      let _tempData = assetOverview;
      _tempData[pool] = {
        deposit: deposit,
        borrow: borrow,
        metadata: { ...params, address: selectedAddress },
        active,
      };
      setAssetOverview({ ..._tempData });
    } catch (error) {
      console.error(error);
    }
  };

  const fetchAssetOverview = useCallback(
    async (pool, params, config) => {
      if (client && selectedAddress)
        if (assetOverview[pool]) {
          let _check = false;
          const metadata = assetOverview[pool]["metadata"];
          if (config?.isCheckMetadata != false) _check = metadata == params && selectedAddress === metadata["address"];
          if (_check) return;
          else await _fetchAssetOverview(pool, params, config?.active);
        } else await _fetchAssetOverview(pool, params, config?.active);
    },
    [client, selectedAddress]
  );

  const _fetchLiquidationHistory = async (pool, params, active) => {
    try {
      const _params = params;
      _params["lending"] = pool;
      _params["address"] = selectedAddress;
      const [assetVolatility, transactionHistory] = await Promise.all([
        client.get("/user/value-volatility-wallet", { params: _params }),
        client.get("/transactions/event-data-wallet", { params: _params }),
      ]);
      let _healthFactor = [];
      let _liquidationChartData = [];
      let _liquidationData = {};

      const _assetData = Object.entries(assetVolatility);
      for (let _data of _assetData) {
        _healthFactor.push([_data[0] * 1000, _data[1].health_factor]);
      }

      const _transactionData = Object.entries(transactionHistory);
      for (let _data of _transactionData) {
        const timestamp = _data[0];
        const data = _data[1];
        if (data.event_type === "LIQUIDATE") {
          _liquidationChartData.push([timestamp * 1000, data.liquidated_collateral_amount]);
          _liquidationData[timestamp] = _data[1];
        }
      }
      let _tempData = liquidationHistory;
      _tempData[pool] = {
        healthFactor: _healthFactor,
        liquidationChart: _liquidationChartData,
        liquidation: _liquidationData,
        metadata: { ...params, address: selectedAddress },
        active,
      };
      setLiquidationHistory({ ..._tempData });
    } catch (error) {
      console.error(error);
    }
  };

  const fetchLiquidationHistory = useCallback(
    async (pool, params, config) => {
      if (client && selectedAddress) {
        if (!params?.from || !params?.to) {
          var currentTime = Math.floor(Date.now() / 1000);
          params.from = currentTime - 86400;
          params.to = currentTime;
        }
        if (liquidationHistory[pool]) {
          let _check = false;
          const metadata = liquidationHistory[pool]["metadata"];
          if (config?.isCheckMetadata != false)
            _check =
              metadata["from"] === params.from &&
              metadata["to"] === params.to &&
              selectedAddress === metadata["address"];
          if (_check) return;
          else await _fetchLiquidationHistory(pool, params, config?.active);
        } else await _fetchLiquidationHistory(pool, params, config?.active);
      }
    },
    [client, selectedAddress]
  );

  const _fetchTotalValueLocked = async (pool, params, active) => {
    try {
      setTotalValueLockedLoading(true);
      const _params = params;
      _params["lending"] = pool;
      _params["address"] = selectedAddress;
      const [tvlData, transactionData] = await Promise.all([
        client.get("/user/tvl-change", { params: _params }),
        client.get("/transactions/event-data-wallet", { params: _params }),
      ]);
      const _tvlData = Object.entries(tvlData);
      let result = [];
      let zones = [];
      let _transactionData = [];
      let index = 0;
      for (let _tvl of _tvlData) {
        const _timestamp = _tvl[0];
        const timestamp = _timestamp * 1000;
        const _transaction = transactionData[_timestamp];
        _transactionData.push(_transaction);
        if (_transaction?.event_type === "DEPOSIT") {
          result.push({ x: timestamp, y: _tvl[1]["tvl"], color: COLOR_ARRAY[0], id: index, name: "Deposit" });
          zones.push({ value: timestamp, color: COLOR_ARRAY[0] });
        } else if (_transaction?.event_type === "BORROW") {
          result.push({ x: timestamp, y: _tvl[1]["tvl"], color: COLOR_ARRAY[1], id: index, name: "Borrow" });
          zones.push({ value: timestamp, color: COLOR_ARRAY[1] });
        } else if (_transaction?.event_type === "WITHDRAW") {
          result.push({ x: timestamp, y: _tvl[1]["tvl"], color: COLOR_ARRAY[2], id: index, name: "Withdraw" });
          zones.push({ value: timestamp, color: COLOR_ARRAY[2] });
        } else if (_transaction?.event_type === "REPAY") {
          result.push({ x: timestamp, y: _tvl[1]["tvl"], color: COLOR_ARRAY[3], id: index, name: "Repay" });
          zones.push({ value: timestamp, color: COLOR_ARRAY[3] });
        } else if (_transaction?.event_type === "LIQUIDATE") {
          result.push({ x: timestamp, y: _tvl[1]["tvl"], color: COLOR_ARRAY[4], id: index, name: "Liquidate" });
          zones.push({ value: timestamp, color: COLOR_ARRAY[4] });
        } else if (_transaction?.event_type === "BUY_LIQUIDATION") {
          result.push({ x: timestamp, y: _tvl[1]["tvl"], color: COLOR_ARRAY[5], id: index, name: "Buy Liquidation" });
          zones.push({ value: timestamp, color: COLOR_ARRAY[5] });
        } else {
          result.push({ x: timestamp, y: _tvl[1]["tvl"], color: "red", id: index, name: "Unknown Event" });
          zones.push({ value: timestamp, color: "red" });
        }
        index++;
      }
      const _tempData = totalValueLocked;
      _tempData[pool] = {
        tvl: result,
        zones: zones.slice(1),
        transactionData: _transactionData,
        metadata: { ...params, address: selectedAddress },
        active,
      };
      setTotalValueLocked({ ..._tempData });
    } catch (error) {
      console.error(error);
    } finally {
      setTotalValueLockedLoading(false);
    }
  };

  const fetchTotalValueLocked = useCallback(
    async (pool, params, config) => {
      if (client && selectedAddress) {
        if (!params?.from || !params?.to) {
          var currentTime = Math.floor(Date.now() / 1000);
          params.from = currentTime - 86400;
          params.to = currentTime;
        }
        if (totalValueLocked[pool]) {
          let _check = false;
          const metadata = totalValueLocked[pool]["metadata"];
          if (config?.isCheckMetadata != false)
            _check =
              metadata["from"] === params.from &&
              metadata["to"] === params.to &&
              selectedAddress === metadata["address"];
          if (_check) return;
          else await _fetchTotalValueLocked(pool, params, config?.active);
        } else await _fetchTotalValueLocked(pool, params, config?.active);
      }
    },
    [client, selectedAddress]
  );

  const _fetchTransactionHistory = async (pool, params, active) => {
    try {
      const _params = params;
      _params["lending"] = pool;
      _params["address"] = selectedAddress;
      const data = await client.get("/transactions/event-data-wallet", { params: _params });
      const _data = Object.entries(data);
      let dataTable = [];
      let deposit = [];
      let withdraw = [];
      let repay = [];
      let borrow = [];
      for (var item of _data) {
        const eventType = item[1].event_type;
        const timestamp = item[0] * 1000;

        let color = COLOR_ARRAY[0];
        if (eventType === "DEPOSIT") {
          deposit.push({ x: timestamp, y: item[1].amount });
          color = COLOR_ARRAY[0];
        } else if (eventType === "BORROW") {
          borrow.push({ x: timestamp, y: item[1].amount });
          color = COLOR_ARRAY[1];
        } else if (eventType === "REPAY") {
          repay.push({ x: timestamp, y: item[1].amount });
          color = COLOR_ARRAY[3];
        } else if (eventType === "WITHDRAW") {
          withdraw.push({ x: timestamp, y: item[1].amount });
          color = COLOR_ARRAY[2];
        }
        if (eventType !== "LIQUIDATE")
          dataTable.push({
            timestamp: timestamp,
            color: color,
            type: item[1].event_type,
            amount: item[1].amount,
            token: item[1].token,
            symbol: item[1].symbol,
            transaction: item[1].tx_hash,
          });
      }
      let _tempData = transactionHistory;
      _tempData[pool] = {
        deposit: deposit,
        borrow: borrow,
        repay: repay,
        withdraw: withdraw,
        dataTable: dataTable,
        metadata: { ...params, address: selectedAddress },
        active,
      };
      setTransactionHistory({ ..._tempData });
    } catch (error) {
      console.error(error);
    }
  };

  const fetchTransactionHistory = useCallback(
    async (pool, params, config) => {
      if (client && selectedAddress) {
        if (!params?.from || !params?.to) {
          var currentTime = Math.floor(Date.now() / 1000);
          params.from = currentTime - 86400;
          params.to = currentTime;
        }
        if (transactionHistory[pool]) {
          let _check = false;
          const metadata = transactionHistory[pool]["metadata"];
          if (config?.isCheckMetadata != false)
            _check =
              metadata["from"] === params.from &&
              metadata["to"] === params.to &&
              selectedAddress === metadata["address"];
          if (_check) return;
          else await _fetchTransactionHistory(pool, params, config?.active);
        } else await _fetchTransactionHistory(pool, params, config?.active);
      }
    },
    [client, selectedAddress]
  );

  useEffect(() => {
    if (address) setSelectedAddress(address);
    else setSelectedAddress(null);
  }, [address]);

  const contextData = useMemo(() => {
    return {
      selectedAddress,
      setSelectedAddress,
      liquidateUserData,
      totalValueLockedLoading,
      chart: {
        assetOverview,
        liquidationHistory,
        totalValueLocked,
        transactionHistory,
      },
      fetch: {
        fetchLiquidateUserData,
        fetchAssetOverview,
        fetchLiquidationHistory,
        fetchTotalValueLocked,
        fetchTransactionHistory,
      },
    };
  }, [
    selectedAddress,
    liquidateUserData,
    totalValueLockedLoading,
    assetOverview,
    liquidationHistory,
    totalValueLocked,
    transactionHistory,
    fetchLiquidateUserData,
    fetchAssetOverview,
    fetchLiquidationHistory,
    fetchTotalValueLocked,
    fetchTransactionHistory,
  ]);

  return <UserMonitoringContext.Provider value={contextData}>{children}</UserMonitoringContext.Provider>;
};

export default UserMonitoringProvider;

export const useUserMonitoringContext = () => React.useContext(UserMonitoringContext);

export const useUserMonitoringSelector = (selectorFn) => {
  const data = useUserMonitoringContext();

  return useMemo(() => selectorFn(data), [data, selectorFn]);
};

export const useUserMonitoringChartSelector = (chartType, pool) => {
  const { chart } = useUserMonitoringContext();
  return useMemo(() => {
    return chart[chartType][pool];
  }, [chart, chartType, pool]);
};
